/**
 * STRUCT CODE カレント診断結果ページ
 * 現在の構造を表示（時期の影響を反映）
 */

import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Info,
  ExternalLink,
  Trophy,
  Award,
  Medal,
  MessageSquare,
  Check,
  LogIn
} from 'lucide-react';
import dynamicStructCodeService from '../services/dynamicStructCodeService';
import structCodeV2Service from '../services/structCodeV2Service';
import billingService from '../services/billingService';
import DesignGapSection from '../components/DesignGapSection';
import CategoryReportSection from '../components/billing/CategoryReportSection';
import BipolarSlider from '../components/viz/BipolarSlider';
import AXIS_DEFS, { getAxisScore } from '../data/axisDefs';
import { getTypeByCode } from '../data/typesData';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

// 軸スライダー一覧 (Card 内で使用)
const AxisStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const AxisStackDivider = styled.div`
  height: 1px;
  background: ${p => p.theme.colors.lineFaint};
  margin: 6px 0;
`;

const renderAxisStack = (axesByBeKey, lang) => (
  <AxisStack>
    {AXIS_DEFS.map((axis, i) => (
      <React.Fragment key={axis.key}>
        {i > 0 && <AxisStackDivider />}
        <BipolarSlider
          label={lang === 'en' ? axis.label_en : axis.label_ja}
          leftPole={lang === 'en' ? axis.pole_left_en : axis.pole_left_ja}
          rightPole={lang === 'en' ? axis.pole_right_en : axis.pole_right_ja}
          score={axesByBeKey?.[axis.beKey] ?? 0}
          color={axis.color}
          showPercent
        />
      </React.Fragment>
    ))}
  </AxisStack>
);

const PageContainer = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 24px 16px;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 16px 12px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin-bottom: 32px;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-bottom: 24px;
  }
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: clamp(2rem, 5vw, 2.8rem);
  color: ${props => props.theme.colors.text};
  margin: 0 0 12px;
  line-height: 1.2;
`;

const Subtitle = styled.div`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.85;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 32px;
  box-shadow: ${props => props.theme.shadows.lg};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 24px;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 16px;
    border-radius: ${props => props.theme.borderRadius.md};
  }
`;

const CardTitle = styled.h2`
  font-family: ${props => props.theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: 1.35rem;
  color: ${props => props.theme.colors.text};
  margin: 0 0 22px;
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.2rem;
    margin-bottom: 18px;
  }
`;

const TypeDisplay = styled.div`
  text-align: center;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 24px 12px;
    gap: 12px;
  }
`;

const TypeLabel = styled.div`
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.75rem;
  color: ${props => props.theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.4em;
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: clamp(10px, 2vw, 22px);
  margin-top: 4px;
  font-family: ${props => props.theme.fonts.mono};
  font-weight: 500;
  font-size: clamp(0.95rem, 1.8vw, 1.2rem);
  letter-spacing: 0.18em;
  color: ${props => props.theme.colors.text};
  opacity: 0.92;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 10px;
    font-size: 0.95rem;
  }
`;

const ScoreCell = styled.span`
  display: inline-block;
  min-width: 3.2ch;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const HeroDivider = styled.span`
  width: 32px;
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, rgba(230,199,126,0.4) 50%, transparent 100%);
  margin: 4px 0;
`;

const Tagline = styled.p`
  margin: 0;
  font-family: ${props => props.theme.fonts.display};
  font-style: italic;
  font-size: 1rem;
  line-height: 1.85;
  color: ${props => props.theme.colors.textSecondary};
  max-width: 32ch;
  word-break: keep-all;
  overflow-wrap: anywhere;
`;

const TypeCode = styled(Link)`
  display: inline-block;
  font-family: ${props => props.theme.fonts.mono};
  font-size: clamp(2rem, 6vw, 3rem);
  font-weight: 600;
  color: ${props => props.theme.colors.starGold};
  margin-bottom: 14px;
  letter-spacing: 0.3em;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.85; }
`;

const AIConsultSection = styled.div`
  margin-top: 24px;
  text-align: center;
`;

const AIConsultButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 15px 30px;
  background: ${props => props.theme.colors.gradAurora};
  color: #FFFFFF;
  border: 1px solid rgba(156,123,201,0.45);
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(156,123,201,0.25);
  transition: all 0.25s ease;
  width: 100%;
  max-width: 360px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 32px rgba(156,123,201,0.35);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.95rem;
    padding: 14px 22px;
  }
`;

const AIConsultHint = styled.p`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 10px;
`;

const TypeName = styled.div`
  font-family: ${props => props.theme.fonts.display};
  font-style: italic;
  font-size: clamp(1.5rem, 3.5vw, 1.9rem);
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  margin-bottom: 8px;
  line-height: 1.2;
`;

const TypeArchetype = styled.div`
  font-size: 1rem;
  color: ${props => props.theme.colors.textSecondary};
  letter-spacing: 0.04em;
  margin-bottom: 18px;
`;

// タイプ適合度の「余白」（1位と2位の適合度差）。
// 適合度と余白は独立した情報: 適合度が高くても隣接タイプと僅差のことが
// あり、その事実を隠さない（TYPE はグラデーション・隣接があり得る設計）。
// 数値のみを示し、質的なラベル（「拮抗」等）は付けない（閾値の恣意を避ける）。
const MarginNote = styled.div`
  margin-top: 8px;
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
`;

const ConfidenceBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: ${props => props.theme.colors.bgPrimary};
  color: ${props => props.theme.colors.starGold};
  border: 1px solid ${props => props.theme.colors.lineGold};
  border-radius: ${props => props.theme.borderRadius.full};
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  font-weight: 500;
`;

const StructCode = styled.div`
  background: ${props => props.theme.colors.bgPrimary};
  border: 1px solid ${props => props.theme.colors.lineFaint};
  padding: 20px;
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.fonts.mono};
  font-size: 1rem;
  letter-spacing: 0.08em;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 24px;
  text-align: center;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 16px;
    font-size: 0.88rem;
    word-break: break-all;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  flex-wrap: wrap;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 10px;
    margin-top: 20px;
  }
`;

const Button = styled(motion.button)`
  padding: 13px 26px;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  border-radius: ${props => props.theme.borderRadius.full};
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: ${props => props.theme.fonts.main};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  ${props => props.primary ? `
    background: ${props.theme.colors.gradGold};
    color: #1A1408;
    border: 1px solid rgba(230,199,126,0.55);
    box-shadow: ${props.theme.shadows.glowSoft};

    &:hover {
      transform: translateY(-1px);
      box-shadow: ${props.theme.shadows.glow};
    }
  ` : `
    background: transparent;
    color: ${props.theme.colors.textSecondary};
    border: 1px solid ${props.theme.colors.lineSoft};

    &:hover {
      color: ${props.theme.colors.starGold};
      border-color: ${props.theme.colors.lineGold};
    }
  `}

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 12px 22px;
    font-size: 0.88rem;
    width: 100%;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  flex-direction: column;
  gap: 20px;
`;

const LoadingText = styled.div`
  font-family: ${props => props.theme.fonts.display};
  font-style: italic;
  font-size: 1.3rem;
  color: ${props => props.theme.colors.textSecondary};
`;

// TOP3タイプ候補用のスタイル
const CandidatesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;

const CandidateItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: ${props => props.rank === 1
    ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 215, 0, 0.05))'
    : props.rank === 2
    ? 'linear-gradient(135deg, rgba(192, 192, 192, 0.15), rgba(192, 192, 192, 0.05))'
    : 'linear-gradient(135deg, rgba(205, 127, 50, 0.15), rgba(205, 127, 50, 0.05))'
  };
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.rank === 1
    ? 'rgba(255, 215, 0, 0.3)'
    : props.rank === 2
    ? 'rgba(192, 192, 192, 0.3)'
    : 'rgba(205, 127, 50, 0.3)'
  };

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 12px;
    padding: 12px;
  }
`;

const RankBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.rank === 1
    ? 'linear-gradient(135deg, #FFD700, #FFA500)'
    : props.rank === 2
    ? 'linear-gradient(135deg, #C0C0C0, #A0A0A0)'
    : 'linear-gradient(135deg, #CD7F32, #8B4513)'
  };
  color: white;
  flex-shrink: 0;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 36px;
    height: 36px;
  }
`;

const CandidateInfo = styled.div`
  flex: 1;
`;

const CandidateType = styled.div`
  font-family: ${props => props.theme.fonts.mono};
  font-weight: 600;
  font-size: 0.92rem;
  letter-spacing: 0.15em;
  color: ${props => props.theme.colors.starGold};
  margin-bottom: 4px;
`;

const CandidateName = styled.div`
  font-family: ${props => props.theme.fonts.display};
  font-style: italic;
  font-size: 1rem;
  color: ${props => props.theme.colors.text};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.92rem;
  }
`;

const CandidateScore = styled.div`
  font-family: ${props => props.theme.fonts.mono};
  font-weight: 500;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  color: ${props => props.theme.colors.starGold};
  padding: 6px 12px;
  background: ${props => props.theme.colors.bgPrimary};
  border: 1px solid ${props => props.theme.colors.lineFaint};
  border-radius: ${props => props.theme.borderRadius.sm};
`;


const SaveIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 18px;
  margin-top: 16px;
  border-radius: ${props => props.theme.borderRadius.full};
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.72rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  font-weight: 500;
  background: ${props => props.$saved
    ? 'rgba(127,184,154,0.10)'
    : props.theme.colors.primaryGlow};
  border: 1px solid ${props => props.$saved
    ? 'rgba(127,184,154,0.35)'
    : props.theme.colors.lineGold};
  color: ${props => props.$saved
    ? props.theme.colors.starMint
    : props.theme.colors.starGold};
`;

const ShareButtons = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 8px;
  }
`;

const ShareButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: 0.88rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-decoration: none;
  transition: all 0.25s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
    opacity: 0.9;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex: 1;
    min-width: 100px;
    padding: 11px 18px;
    font-size: 0.85rem;
  }
`;

const XShareButton = styled(ShareButton)`
  background: #000000;
  color: #FFFFFF;
`;


const LineShareButton = styled(ShareButton)`
  background: #06C755;
  color: #FFFFFF;
`;

// SNSアイコンコンポーネント
const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);


const LineIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
  </svg>
);

function DynamicResultPage() {
  const { diagnosisId } = useParams();
  const location = useLocation();
  const { t, lang } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [diagnosis, setDiagnosis] = useState(null);
  const [typeInfo, setTypeInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);

  useEffect(() => {
    const fetchDiagnosis = async () => {
      // まずstateからのデータを確認
      if (location.state?.diagnosis) {
        const stateData = location.state.diagnosis;
        console.log('Using state diagnosis:', stateData);
        console.log('State diagnosis keys:', Object.keys(stateData || {}));
        // 各キーの値もログ出力
        Object.keys(stateData || {}).forEach(key => {
          console.log(`  ${key}:`, typeof stateData[key], stateData[key]);
        });
        setDiagnosis(stateData);
        setLoading(false);
        return;
      }

      // stateがない場合はAPIから取得
      if (diagnosisId) {
        try {
          console.log('Fetching diagnosis from API, diagnosisId:', diagnosisId);
          const result = await dynamicStructCodeService.getDiagnosis(diagnosisId);
          console.log('API Response:', result);
          console.log('API Response keys:', Object.keys(result || {}));

          // APIがエラーを返した場合（detailキーがある場合）
          if (result.detail) {
            throw new Error(result.detail);
          }

          setDiagnosis(result);
          setLoading(false);
        } catch (err) {
          console.error('診断結果の取得に失敗:', err);
          setError(err.message || '診断結果を取得できませんでした');
          setLoading(false);
        }
      }
    };
    fetchDiagnosis();
  }, [diagnosisId, location.state]);

  // 課金ステータスを取得
  useEffect(() => {
    if (isAuthenticated) {
      billingService.getIndividualStatus()
        .then(setBillingStatus)
        .catch(() => {});
    }
  }, [isAuthenticated]);

  // 個人AI行動特性分析 (PersonalAiReport) は消費者の結果ページからは撤去。
  // 深い個別読みは AI チャットが担う。PersonalAiReport は B2B (MemberDetailPage) で
  // のみ使用する。

  // タイプ情報をAPIから取得（エンコーディング問題回避）
  useEffect(() => {
    const fetchTypeInfo = async () => {
      if (diagnosis) {
        const typeCode = diagnosis.current?.type || diagnosis.natal?.type || diagnosis.legacy?.struct_type;
        if (typeCode) {
          try {
            const info = await structCodeV2Service.getTypeDetails(typeCode, lang);
            setTypeInfo(info);
          } catch (err) {
            console.error('タイプ情報の取得に失敗:', err);
          }
        }
      }
    };
    fetchTypeInfo();
  }, [diagnosis, lang]);

  const generateAIPrompt = () => {
    // サーバ側で認証済ユーザーの軸・天体・Design Gap は自動補完されるので、
    // フロントから長文のコンテキスト送信は不要。
    // 短い自然な開幕 1 文に絞る。
    return '診断結果について、読み解いてもらえますか。';
  };

  // 「結果を AI に相談する」: 押した時点で、この診断結果を文脈にしたチャットを開始する。
  const handleAIConsult = () => {
    const params = new URLSearchParams();
    const code = diagnosis?.struct_code || '';
    if (code) params.set('struct_code', code);
    const prompt = generateAIPrompt();
    if (prompt) params.set('prompt', prompt);
    params.set('from_result', '1');
    navigate(`/chat?${params.toString()}`);
  };

  // SNSシェア用テキストを生成
  const generateShareText = () => {
    if (!diagnosis) return '';

    const code = diagnosis.struct_code || '';
    const candidates = diagnosis.top3_types || diagnosis.legacy?.type_candidates || [];
    const topType = candidates[0];
    const typeName = topType?.name || typeInfo?.name || '';
    const score = topType?.score ? `${(topType.score * 100).toFixed(0)}%` : '';

    return `【STRUCT CODE診断結果】
私のタイプは「${typeName}」（適合度${score}）でした！

${code}

あなたも診断してみませんか？
#STRUCTCODE #性格診断 #自己分析`;
  };

  // シェアURL生成
  const getShareUrls = () => {
    const text = encodeURIComponent(generateShareText());
    const url = encodeURIComponent('https://www.struct-code.jp/current-diagnosis');

    return {
      x: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      line: `https://social-plugins.line.me/lineit/share?url=${url}&text=${text}`,
    };
  };


  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          >
            <Sparkles size={48} color="#6366F1" />
          </motion.div>
          <LoadingText>{t('動的診断結果を読み込んでいます...', 'Loading diagnosis result...')}</LoadingText>
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error || !diagnosis) {
    return (
      <PageContainer>
        <Card>
          <CardTitle>{t('エラー', 'Error')}</CardTitle>
          <p>{error || t('診断結果が見つかりません', 'Diagnosis result not found')}</p>
          <Button primary as={Link} to="/current-diagnosis">
            {t('新しい診断を開始', 'Start New Diagnosis')}
          </Button>
        </Card>
      </PageContainer>
    );
  }

  // Dynamic APIレスポンス形式
  const { natal, current, design_gap, legacy } = diagnosis;
  // ネイタルAPI（v2）レスポンス形式へのフォールバック
  const { struct_type, axes, type_info, type_candidates } = diagnosis;

  const axisNames = ['起動軸', '判断軸', '選択軸', '共鳴軸', '自覚軸'];
  // デバッグログ
  console.log('=== Dynamic Diagnosis Data ===');
  console.log('diagnosis:', diagnosis);
  console.log('natal:', natal, 'current:', current, 'legacy:', legacy);
  console.log('struct_type:', struct_type, 'axes:', axes);

  // 軸スコアを取得（Dynamic形式 or ネイタル形式）
  let currentAxes = {};
  let natalAxes = {};
  const axisKeysMap = ['activation', 'judgment', 'choice', 'resonance', 'awareness'];
  if (current?.sds || natal?.sds) {
    // Dynamic API形式: sds配列から
    const currentSds = current?.sds || [0.5, 0.5, 0.5, 0.5, 0.5];
    const natalSds = natal?.sds || currentSds;
    axisNames.forEach((axis, index) => {
      currentAxes[axis] = currentSds[index] || 0;
      natalAxes[axis] = natalSds[index] || 0;
    });
  } else if (axes) {
    // ネイタルAPI形式: axes辞書から直接
    currentAxes = axes;
    natalAxes = axes;
  } else {
    axisNames.forEach((axis) => {
      currentAxes[axis] = 0.5;
      natalAxes[axis] = 0.5;
    });
  }

  // SpectrumBar用に正規化（0-1のキー名形式に変換）
  const currentSpectrumAxes = {};
  const natalSpectrumAxes = {};
  axisNames.forEach((axis, index) => {
    currentSpectrumAxes[axisKeysMap[index]] = (currentAxes[axis] || 0) * 1000;
    natalSpectrumAxes[axisKeysMap[index]] = (natalAxes[axis] || 0) * 1000;
  });

  // タイプコードを取得（Dynamic形式 or ネイタル形式）
  const typeCode = current?.type || natal?.type || legacy?.struct_type || struct_type || '';

  // STRUCT CODE文字列
  const structCode = diagnosis.struct_code || legacy?.struct_code || '';

  // タイプ候補一覧（Dynamic形式 or ネイタル形式）
  const typeCandidates = diagnosis.top3_types || legacy?.type_candidates || type_candidates || [];

  // 適合度（TOP1のスコアを使用して一貫性を保つ）
  const confidence = typeCandidates?.[0]?.score || legacy?.confidence || diagnosis.confidence || 0;

  // タイプ詳細情報（Dynamic形式 or ネイタル形式）
  const legacyTypeDetail = legacy?.type_detail || type_info || {};

  // archetype は top3_types の最初の要素から取得（Dynamic形式）
  // または type_info.archetype（ネイタル形式）から取得
  const archetype = typeCandidates?.[0]?.archetype || type_info?.archetype || legacyTypeDetail?.archetype || '-';

  return (
    <PageContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Header>
        <Title>{t('あなたのSTRUCT CODE', 'Your STRUCT CODE')}</Title>
        <Subtitle>{t('内面構造診断結果', 'Inner Structure Analysis Result')}</Subtitle>
      </Header>

      <Grid>
        <Card>
          <TypeDisplay>
            <TypeLabel>TYPE</TypeLabel>
            <TypeCode to={`/types/${typeCode}`}>{typeCode}</TypeCode>
            <ScoreRow>
              {AXIS_DEFS.map(axis => {
                const v = getAxisScore(currentAxes, axis);
                const scaled = v === null || v === undefined ? 0 : Math.round(v * 1000);
                return <ScoreCell key={axis.key}>{String(scaled).padStart(3, '0')}</ScoreCell>;
              })}
            </ScoreRow>
            <HeroDivider />
            <TypeName>{typeInfo?.name || legacyTypeDetail?.label || current?.type_name || getTypeByCode(typeCode, lang)?.name || typeCode}</TypeName>
            <TypeArchetype>{archetype || getTypeByCode(typeCode, lang)?.archetype || ''}</TypeArchetype>
            {(() => {
              const tagline = getTypeByCode(typeCode, lang)?.tagline;
              return tagline ? <Tagline>{tagline}</Tagline> : null;
            })()}
            <ConfidenceBadge>
              {t('適合度', 'Match')} {(confidence * 100).toFixed(1)}%
            </ConfidenceBadge>
            {(() => {
              const s1 = typeCandidates?.[0]?.score;
              const s2 = typeCandidates?.[1]?.score;
              const t2 = typeCandidates?.[1]?.type;
              if (typeof s1 !== 'number' || typeof s2 !== 'number' || !t2) return null;
              const gap = ((s1 - s2) * 100).toFixed(1);
              return (
                <MarginNote>
                  {t(`次点 ${t2} との差 ${gap}pt`, `Gap to runner-up ${t2}: ${gap}pt`)}
                </MarginNote>
              );
            })()}

            <StructCode>
              {structCode}
            </StructCode>

            <AIConsultSection>
              <AIConsultButton
                onClick={handleAIConsult}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <MessageSquare size={20} />
                {t('結果をAIに相談する', 'Consult AI About Results')}
              </AIConsultButton>
              <AIConsultHint>
                {t('AIがあなたの診断結果を分析します', 'AI will analyze your diagnosis results')}
              </AIConsultHint>
            </AIConsultSection>

            <ShareButtons style={{ justifyContent: 'center', marginTop: '20px' }}>
              <XShareButton
                href={getShareUrls().x}
                target="_blank"
                rel="noopener noreferrer"
              >
                <XIcon />
                {t('Xでシェア', 'Share on X')}
              </XShareButton>
              <LineShareButton
                href={getShareUrls().line}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LineIcon />
                LINE
              </LineShareButton>
            </ShareButtons>
          </TypeDisplay>

          {isAuthenticated ? (
            <SaveIndicator $saved>
              <Check size={16} />
              {t('結果は自動保存されました', 'Result saved automatically')}
            </SaveIndicator>
          ) : (
            <SaveIndicator>
              <LogIn size={16} />
              {t('ログインすると結果が保存されます', 'Login to save your results')}
            </SaveIndicator>
          )}

          <ActionButtons style={{ justifyContent: 'center' }}>
            <Button primary as={Link} to={`/types/${typeCode}`}>
              <Info size={18} />
              {t('タイプ詳細を見る', 'View Type Details')}
            </Button>
            <Button as={Link} to="/types">
              {t('他のタイプを見る', 'View Other Types')}
              <ExternalLink size={16} />
            </Button>
          </ActionButtons>
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Card>
            <CardTitle>
              <Sparkles size={20} />
              {t('現在の構造', 'Current Structure')}
            </CardTitle>
            {renderAxisStack(currentAxes, lang)}
          </Card>

          <Card>
            <CardTitle>
              <Sparkles size={20} />
              {t('本来の構造', 'Innate Structure')}
            </CardTitle>
            {renderAxisStack(natalAxes, lang)}
          </Card>
        </div>
      </Grid>

      {/* Design Gap セクション */}
      <DesignGapSection
        natal={natal}
        current={current}
        designGap={design_gap}
      />

      {/* TOP3 タイプ候補セクション */}
      <Card style={{ marginBottom: '24px' }}>
        <CardTitle>
          <Trophy size={20} />
          {t('タイプ候補 TOP3', 'Top 3 Type Candidates')}
        </CardTitle>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '0.95rem' }}>
          {t('あなたの特性に近いタイプを適合度順に表示しています', 'Types closest to your characteristics, ranked by match rate')}
        </p>
        {typeCandidates && typeCandidates.length > 0 ? (
          <CandidatesList>
            {typeCandidates.slice(0, 3).map((candidate, index) => {
              const rank = index + 1;
              const RankIcon = rank === 1 ? Trophy : rank === 2 ? Award : Medal;
              return (
                <CandidateItem
                  key={candidate.type || index}
                  rank={rank}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <RankBadge rank={rank}>
                    <RankIcon size={20} />
                  </RankBadge>
                  <CandidateInfo>
                    <CandidateType>
                      {candidate.type}
                      {rank === 1 && t(' (判定結果)', ' (Result)')}
                    </CandidateType>
                    <CandidateName>
                      {candidate.name || '-'} - {candidate.archetype || '-'}
                    </CandidateName>
                  </CandidateInfo>
                  <CandidateScore>
                    {((candidate.score || 0) * 100).toFixed(1)}%
                  </CandidateScore>
                </CandidateItem>
              );
            })}
          </CandidatesList>
        ) : (
          <p style={{ color: 'var(--text-secondary)' }}>
            {t('タイプ候補データがありません', 'No type candidate data available')}
          </p>
        )}
      </Card>

      {/* Category Reports Section */}
      {isAuthenticated && (
        <CategoryReportSection billingStatus={billingStatus} />
      )}

    </PageContainer>
  );
}

export default DynamicResultPage;
